import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getComicsByCategory, formatViews, type Comic } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | undefined>(undefined);
  const [relatedComics, setRelatedComics] = useState<Comic[]>([]);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [activeChapterTab, setActiveChapterTab] = useState<'all' | 'new'>('all');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const comicId = parseInt(id || '0', 10);
    const found = getComicById(comicId);
    setComic(found);
    if (found) {
      const related = getComicsByCategory(found.categorySlug)
        .filter((c) => c.id !== found.id)
        .slice(0, 4);
      setRelatedComics(related);
    }
  }, [id]);

  if (!comic) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-6">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-800">漫画未找到</h2>
          <p className="mt-2 text-sm text-foreground-500">抱歉，您要查看的漫画不存在或已被移除</p>
          <Link
            to="/category/all"
            className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
          >
            浏览其他漫画
          </Link>
        </div>
      </div>
    );
  }

  const displayedChapters = activeChapterTab === 'new'
    ? comic.chapters.slice(0, 3)
    : comic.chapters;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    genre: comic.category,
    inLanguage: 'zh-CN',
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      ratingCount: comic.favorites,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `《${comic.title}》是什么类型的韩国漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》是一部${comic.category}类型的韩国漫画，由${comic.author}创作，属于${comic.tags.join('、')}风格的作品。`,
        },
      },
      {
        '@type': 'Question',
        name: `《${comic.title}》在哪里可以免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在韩漫网站免费阅读《${comic.title}》，支持下拉式流畅浏览体验，随时随地畅享漫画乐趣。`,
        },
      },
      {
        '@type': 'Question',
        name: `《${comic.title}》的更新状态是什么？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》目前状态为「${comic.status}」，最新更新于${comic.updatedAt}。`,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 免费阅读韩国漫画`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}。免费阅读韩国漫画，韩漫网站提供下拉式流畅阅读体验。${comic.category}漫画推荐。`}
        keywords={`${comic.title}, ${comic.category}漫画, 韩国漫画, 韩漫网站, 免费阅读漫画, 下拉式, ${comic.tags.join(', ')}`}
        canonicalPath={`/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        schema={{ ...articleSchema, ...faqSchema }}
      />

      <div className="pt-16">
        <div className="w-full px-4 md:px-8 py-6 bg-background-50 border-b border-background-200/60">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '漫画分类', path: '/category/all' },
                { label: comic.category, path: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-full md:w-60">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 免费阅读韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {comic.title}
                </h1>
                <p className="mt-2 text-sm text-foreground-500">作者：{comic.author}</p>

                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className="px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full">
                    {comic.category}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    comic.isCompleted
                      ? 'bg-secondary-200 text-secondary-800'
                      : 'bg-primary-100 text-primary-800'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-200 text-foreground-600 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-5 mt-5">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-sm font-semibold text-foreground-800">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">评分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-500"></i>
                    <span className="text-sm font-semibold text-foreground-800">{formatViews(comic.views)}</span>
                    <span className="text-xs text-foreground-400">阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-heart-line text-foreground-500"></i>
                    <span className="text-sm font-semibold text-foreground-800">{formatViews(comic.favorites)}</span>
                    <span className="text-xs text-foreground-400">收藏</span>
                  </div>
                </div>

                <div className="mt-6">
                  <div className={`text-sm text-foreground-600 leading-relaxed ${showFullDesc ? '' : 'line-clamp-4'}`}>
                    {comic.description}
                  </div>
                  <button
                    onClick={() => setShowFullDesc(!showFullDesc)}
                    className="mt-2 text-xs text-primary-600 hover:text-primary-700 cursor-pointer font-medium transition-colors"
                  >
                    {showFullDesc ? '收起' : '展开全部'}
                  </button>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="#chapters"
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                  </a>
                  <button className="px-6 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-1.5"></i>加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="chapters" className="w-full px-4 md:px-8 py-10 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-foreground-900">章节列表</h2>
              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveChapterTab('all')}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer ${
                    activeChapterTab === 'all'
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  全部({comic.chapters.length})
                </button>
                <button
                  onClick={() => setActiveChapterTab('new')}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer ${
                    activeChapterTab === 'new'
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  最新(3)
                </button>
              </div>
            </div>

            <div className="space-y-1">
              {displayedChapters.map((ch) => (
                <div
                  key={ch.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-300 cursor-pointer transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-400 font-mono">第{ch.number}话</span>
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors font-medium">
                      {ch.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-400">{ch.updatedAt}</span>
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                  </div>
                </div>
              ))}
            </div>

            {comic.chapters.length > 3 && activeChapterTab === 'new' && (
              <button
                onClick={() => setActiveChapterTab('all')}
                className="mt-4 w-full py-3 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer transition-colors text-center"
              >
                查看全部 {comic.chapters.length} 个章节
              </button>
            )}
          </div>
        </div>

        {relatedComics.length > 0 && (
          <div className="w-full px-4 md:px-8 py-14 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}